import React, { useState } from "react";
import { useUser } from "../../context/UserContext";
import styles from "./Sidebar.module.css";
import {
  FaBed,
  FaUser,
  FaMoneyBillAlt,
  FaCog,
  FaSignOutAlt,
  FaFileAlt,
  FaChevronDown,
  FaChevronRight,
  FaUsers,
  FaBuilding,
  FaImages,
  FaTags,
  FaStar,
  FaUtensils,
} from "react-icons/fa";
import { MdDashboard } from "react-icons/md";
import { NavLink, useNavigate } from "react-router-dom";
import api from "../../api/axois";

const Sidebar = ({ open, onClose }) => {
  const { setUser, user } = useUser();
  const navigate = useNavigate();
  const [openSubmenus, setOpenSubmenus] = useState({});

  const toggleSubmenu = (submenuKey) => {
    setOpenSubmenus(prev => ({
      ...prev,
      [submenuKey]: !prev[submenuKey]
    }));
  };

  const handleLogout = async () => {
    const isKitchenStaff = user?.role === "kitchen_staff";

    try {
      try {
        await api.post(isKitchenStaff ? "/chopnation/logout" : "/logout");
      } catch (err) {
        // ignore network errors on logout
      }
      setUser(null);
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      navigate(isKitchenStaff ? "/dining" : "/login");
    } catch (err) {
      alert("Logout failed.");
    }
  };

  const navLinkClass = ({ isActive }) =>
    isActive ? `${styles.navLink} ${styles.navLinkActive}` : styles.navLink;

  return (
    <aside className={`${styles.sidebar} ${open ? styles.open : ""}`}>
        {/* Header / Brand */}
        <div className={styles.brand}>
          <span className={styles.brandIcon}>A</span>
          <span className={styles.brandName}>Aurelia</span>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close menu"
          >
            &times;
          </button>
        </div>

        <nav className={styles.nav}>
          <p className={styles.sectionLabel}>Main</p>
          <ul className={styles.navList}>
            <li>
              <NavLink to="/admin/BookingManagement" className={navLinkClass}>
                <MdDashboard className={styles.navIcon} />
                <span>Dashboard</span>
              </NavLink>
            </li>

            <li>
              <NavLink to="/admin/payments" className={navLinkClass}>
                <FaMoneyBillAlt className={styles.navIcon} />
                <span>Payments</span>
              </NavLink>
            </li>
          </ul>

          <p className={styles.sectionLabel}>Property</p>
          <ul className={styles.navList}>
            <li>
              <NavLink to="/admin/orders" className={navLinkClass}>
                <FaUtensils className={styles.navIcon} />
                <span>Food Orders</span>
              </NavLink>
            </li>
            <li>
              <button
                className={`${styles.submenuToggle} ${openSubmenus.rooms ? styles.submenuToggleOpen : ""}`}
                onClick={() => toggleSubmenu("rooms")}
                aria-expanded={!!openSubmenus.rooms}
              >
                <span className={styles.submenuToggleLeft}>
                  <FaBuilding className={styles.navIcon} />
                  <span>Rooms</span>
                </span>
                <FaChevronDown className={styles.chevron} />
              </button>
              <ul className={`${styles.submenu} ${openSubmenus.rooms ? styles.submenuOpen : ""}`}>
                <li>
                  <NavLink to="/admin/RoomManagement" className={navLinkClass}>
                    <FaBed className={styles.navIcon} />
                    <span>Room Management</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/RoomTypeManagement" className={navLinkClass}>
                    <FaBed className={styles.navIcon} />
                    <span>Room Types</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/gallery" className={navLinkClass}>
                    <FaImages className={styles.navIcon} />
                    <span>Gallery</span>
                  </NavLink>
                </li>
              </ul>
            </li>
          </ul>

          <p className={styles.sectionLabel}>People</p>
          <ul className={styles.navList}>
            <li>
              <button
                className={`${styles.submenuToggle} ${openSubmenus.users ? styles.submenuToggleOpen : ""}`}
                onClick={() => toggleSubmenu("users")}
                aria-expanded={!!openSubmenus.users}
              >
                <span className={styles.submenuToggleLeft}>
                  <FaUsers className={styles.navIcon} />
                  <span>Users & Partners</span>
                </span>
                <FaChevronDown className={styles.chevron} />
              </button>
              <ul className={`${styles.submenu} ${openSubmenus.users ? styles.submenuOpen : ""}`}>
                <li>
                  <NavLink to="/admin/UserManagement" className={navLinkClass}>
                    <FaUser className={styles.navIcon} />
                    <span>Users</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/affiliate" className={navLinkClass}>
                    <FaMoneyBillAlt className={styles.navIcon} />
                    <span>Affiliates</span>
                  </NavLink>
                </li>
              </ul>
            </li>
          </ul>

          <p className={styles.sectionLabel}>Administration</p>
          <ul className={styles.navList}>
            <li>
              <button
                className={`${styles.submenuToggle} ${openSubmenus.admin ? styles.submenuToggleOpen : ""}`}
                onClick={() => toggleSubmenu("admin")}
                aria-expanded={!!openSubmenus.admin}
              >
                <span className={styles.submenuToggleLeft}>
                  <FaCog className={styles.navIcon} />
                  <span>Admin</span>
                </span>
                <FaChevronDown className={styles.chevron} />
              </button>
              <ul className={`${styles.submenu} ${openSubmenus.admin ? styles.submenuOpen : ""}`}>
                <li>
                  <NavLink to="/admin/policies" className={navLinkClass}>
                    <FaFileAlt className={styles.navIcon} />
                    <span>Policies</span>
                  </NavLink>
                </li>
                {user?.role === "super_admin" && (
                  <li>
                    <NavLink to="/admin/action-log" className={navLinkClass}>
                      <FaCog className={styles.navIcon} />
                      <span>Action Log</span>
                    </NavLink>
                  </li>
                )}
                <li>
                  <NavLink to="/admin/settings" className={navLinkClass}>
                    <FaCog className={styles.navIcon} />
                    <span>Settings</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/promotions" className={navLinkClass}>
                    <FaTags className={styles.navIcon} />
                    <span>Promotions</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/admin/reviews" className={navLinkClass}>
                    <FaStar className={styles.navIcon} />
                    <span>Reviews</span>
                  </NavLink>
                </li>
              </ul>
            </li>
          </ul>
        </nav>

        <div className={styles.sidebarFooter}>
          <button className={styles.logoutBtn} onClick={handleLogout}>
            <FaSignOutAlt className={styles.navIcon} />
            <span>Log Out</span>
          </button>
        </div>
      </aside>
  );
};

export default Sidebar;

